/**
 * The kitchen display while the cloud is away (08-clients.md, F10 step 3): the tickets
 * come from the outlet hub when this device can reach one, else from this device's own
 * store (a till or KDS box that also took the orders). Both use the hub's `KdsBoard`, so
 * a ticket looks and behaves the same from either source, in the cloud's `kds/tickets` shape.
 *
 * Local store: `kds:local:{outlet}` in the offline kv. A till writes every ticket it
 * sends offline into it (and relays the same ticket to the hub); the KDS screen reads the
 * hub first and falls back to it. Start / bump / recall go to the hub, or to the local
 * board when there is none. Bumps made offline stay offline: the sync has no KDS command
 * (the cloud files the KOT as displayed), so `clearLocalKds` runs when the queue drains and
 * the screen goes back to the cloud's tickets (pending: a kitchen bump command in the sync).
 */
import { KdsBoard, type KdsRecord } from '@qrkey/outlet-hub/kds';
import type { KdsTicketsView } from '@qrkey/outlet-hub/client';
import type { HubTicketInput } from '@qrkey/outlet-hub/protocol';
import type { KdsTicketView } from '@qrkey/contracts';
import { kvGet, kvSet, db } from './db.js';
import { resolveHubClient, type HubLinkDeps } from './hub-link.js';

const key = (outletId: string) => `kds:local:${outletId}`;

async function load(outletId: string): Promise<KdsBoard> {
  return new KdsBoard((await kvGet<KdsRecord[]>(key(outletId))) ?? []);
}

async function save(outletId: string, board: KdsBoard): Promise<void> {
  await kvSet(key(outletId), board.snapshot());
}

/** Keep a ticket this device just sent (a missing KOT number shows as 0 until the sync numbers it). */
export async function recordLocalTickets(
  outletId: string,
  tickets: readonly HubTicketInput[],
  now = Date.now(),
): Promise<void> {
  if (tickets.length === 0) return;
  const board = await load(outletId);
  for (const t of tickets) board.add({ ...t, kotNo: t.kotNo ?? 0 } as KdsTicketView, now);
  await save(outletId, board);
}

export async function clearLocalKds(outletId: string): Promise<void> {
  await db().kv.delete(key(outletId));
}

export interface OfflineKds {
  source: 'hub' | 'local';
  tickets: KdsTicketsView;
}

export async function readOfflineKds(
  outletId: string,
  stationId: string | null,
  deps: HubLinkDeps & { now?: number } = {},
): Promise<OfflineKds> {
  const hub = await resolveHubClient(deps);
  if (hub) {
    try {
      return { source: 'hub', tickets: await hub.kdsTickets(stationId) };
    } catch {
      // The hub is not answering: this device's own tickets are what it has.
    }
  }
  const board = await load(outletId);
  return { source: 'local', tickets: board.list(stationId, deps.now ?? Date.now()) };
}

export type KdsAct = 'start' | 'bump' | 'recall';

/** Start, bump or recall a ticket (or only some of its items) with the cloud away. */
export async function actOfflineKds(
  outletId: string,
  ticketId: string,
  action: KdsAct,
  lineIds?: readonly string[],
  deps: HubLinkDeps & { now?: number } = {},
): Promise<'hub' | 'local'> {
  const hub = await resolveHubClient(deps);
  if (hub) {
    try {
      await hub.kdsAct(ticketId, action, lineIds);
      return 'hub';
    } catch {
      // Fall through to the local board.
    }
  }
  const board = await load(outletId);
  board.act(ticketId, action, deps.now ?? Date.now(), lineIds);
  await save(outletId, board);
  return 'local';
}
